// ============================================================
// TILT — TABLE 01: THE FOUNDRY   ★★★ THE MIDWAY
// ------------------------------------------------------------
// KIND: score  (a carnival with a boss chained under it)
//
// ══════════════════════════════════════════════════════════════
//  WHAT CHANGED, AND THE ONE THING THAT DIDN'T
// ══════════════════════════════════════════════════════════════
//
// The old header said the first table must be BORING, and it was
// right, and this rebuild does not repeal it:
//
//     YOU CANNOT BREAK A RULE THE PLAYER NEVER LEARNED.
//
// If ball one is an anti-gravity nightmare, then a power that
// breaks physics means nothing, because the player never met
// physics. So DORMANT is still DORMANT. Nothing on this table
// moves on ball one except the ball and your flippers. That law
// is untouched and it is load-bearing.
//
// But "boring" was doing two jobs and only one of them was real:
//
//   1. TEACH THE BASELINE.       ← keep forever.
//   2. GIVE THE PLAYER NOTHING   ← not a law. just a consequence
//      TO LOOK AT.                 nobody noticed we'd shipped.
//
// The Foundry was EMPTY. Three bumpers in a ROW, a bank, two
// ramps, an orbit, and a lot of dark blue nothing. A ball crossed
// it in a straight line because there was nothing in the way to
// make it turn. A pinball table's job is to make a ball go
// somewhere it did not ask to go, and this one mostly didn't.
//
// So: THE MIDWAY. Same fire underneath — the Forgemaster is intact
// down to the beat of its hammers — but the thing built on top of
// it is a carnival. More bumpers, in a ring instead of a line.
// More posts. Ramps. A wheel that spins when you earn it. And a
// room you cannot get into.
//
// ══════════════════════════════════════════════════════════════
//  THE ATTRACTIONS
// ══════════════════════════════════════════════════════════════
//
//   ▓ THE BIG TOP        Five bumpers in a RING around the centre
//                        of the table. The old nest was three in a
//                        line: a ball entered, bounced twice, and
//                        left. A ring has no exit lane. A ball that
//                        gets in RATTLES until it finds its own way
//                        out. Charge, loud and free, and it is the
//                        first thing every launch meets.
//
//   ▓ THE CAROUSEL       Three posts, off-axis, in the mid-band.
//                        Chaos with a shape to it. Their job is to
//                        make a returning ball's path unguessable,
//                        not to end it.
//
//   ▓ THE LOOP-THE-LOOP  Bottom-left, where the old left ramp was.
//                        The ramp and the loop could never share
//                        that corner (a hammer ran through the old
//                        loop's channel) — so the ramp went, and
//                        the loop got the room it needed.
//
//   ▓ THE HIGH STRIKER   The right ramp → the crucible. The old
//                        jackpot, untouched, because it worked. It
//                        stays OFF the deflector's flight path —
//                        the old one sat under it, so every launch
//                        scored the jackpot for free on ball one.
//                        A money shot you cannot miss is a cutscene.
//
//   ▓ THE SHOOTING       The four-target bank. Complete it and the
//     GALLERY            crucible lights.
//
//   ▓ THE GHOST TRAIN    ★ THE SECRET. A sealed room over the Big
//                        Top with exactly one door, and the door is
//                        a GHOSTABLE FLOOR. There is no shot that
//                        opens it. There is no bank, no angle, no
//                        lucky rattle, and there never will be. You
//                        phase through the floor or you never see
//                        the inside of that room — and the inside
//                        of that room is the biggest number on the
//                        table.
//
// ══════════════════════════════════════════════════════════════
//  ★ THE TICKET LADDER — the unlockables
// ══════════════════════════════════════════════════════════════
//
// Every attraction pays a TICKET the first time you work it. Not
// the first time you HIT it — the first time you work it. Tickets
// open the midway up:
//
//     🎟 3  THE WHEEL SPINS   multiplier ×2
//     🎟 5  THE GATE OPENS    the vault goes live
//     🎟 7  THE MIDWAY ROARS  every bumper kicks 40% harder
//
// THE SEVEN ATTRACTIONS:
//   1 THE BIG TOP          the bumper ring
//   2 THE CAROUSEL         the posts
//   3 THE SHOOTING GALLERY the target bank, completed
//   4 THE HELTER-SKELTER   either ramp
//   5 THE GRAND ORBIT      the orbit, completed
//   6 THE SPINNER          the spinner
//   7 THE GHOST TRAIN      ★ the vault -- the SEVENTH ticket
//
// ⚠ THE SEVENTH TICKET IS THE VAULT, AND THAT IS NOT DECORATION.
//   My first draft had ROAR at 7 and only SIX attractions to earn
//   tickets from. The sim ran 120 games and reported:
//
//       🎟3 WHEEL SPINS : 118/120
//       🎟5 GATE OPENS  : 114/120
//       🎟7 MIDWAY ROARS:   0/120     <-- unreachable. ever.
//
//   Six attractions, a seven-ticket unlock. I had shipped DEAD
//   CONTENT: a reward with no path to it, which no player would
//   ever have reported as a bug -- they would simply never have
//   seen it, and would never have known there was anything to see.
//
//   It is the `glowRect` failure in a new costume: a thing that
//   silently does not exist, that looks perfectly fine in the
//   source, and that only a sim asking a flat question ever finds.
//
//   And the fix is BETTER than the bug: the ladder now ENDS on the
//   secret. GATE (5) opens the Ghost Train; the Ghost Train itself
//   is ticket 7. So the final unlock on the table can only be
//   bought with the one shot that requires a power.
//
//       THE LAST DOOR ON THE MIDWAY IS OPENED BY BREAKING PHYSICS.
//
// ── WHY TICKETS AND NOT JUST POINTS ─────────────────────────
// Because the failure mode of every score table ever shipped is
// that a player finds the single best shot and repeats it until
// the ball drains. That is not playing the table. That is playing
// ONE OBJECT ON the table, and the other nine might as well not
// exist.
//
// A ticket cannot be farmed. The bumper ring pays exactly one, no
// matter how long you rattle in it. So the ONLY way to open the
// gate is to go and touch things you have not touched yet —
// which is another way of saying: THE UNLOCK IS A REASON TO USE
// THE WHOLE TABLE, and it is the only mechanic here that could
// have made the geometry worth building.
//
// ⚠ NOTE THE ORDER. The GATE (the vault) is the FIFTH ticket, not
//   the first. You cannot stumble into the secret. By the time the
//   Ghost Train is even live, you have already been made to work
//   five different attractions, which means you have already
//   LEARNED THE TABLE. The secret is the exam, not the tutorial.
//   Same shape as the boss: aggression is earned, and so is depth.
//
// ⚠ TICKETS LIVE IN _state AND `vaults`/`kills`/`jackpots`
//   ESCALATE. See the singleton warning further down. This is
//   EXACTLY the counter that once paid Spite fifty million points.
//
// ══════════════════════════════════════════════════════════════
//  THE FORGEMASTER — FOUR PHASES (unchanged; it works)
// ══════════════════════════════════════════════════════════════
//
//   ▓ DORMANT   heat 0     Pure pinball. Nothing moves but you.
//   ▓ STOKING   heat 4     The BELLOWS breathe: gravity pulses on
//                          a sine across the middle band. Your
//                          shots aren't wrong — they're EARLY or
//                          LATE. That's a rhythm, and a rhythm can
//                          be learned. Never noise.
//   ▓ AWAKE     heat 9     The PISTONS swing. Three hammers, in
//                          strict sequence, on a beat. A shot you
//                          had lined up is now a wall.
//   ▓ MELTDOWN  heat 15    Everything, faster, and the HEART opens.
//                          22 seconds to kill it or it cools.
//
// The boss has no damage and needs none: every weapon it owns is a
// weapon that DRAINS YOUR BALL, which is the only currency pinball
// has ever had. It doesn't hit you. It takes the table away from
// you, one shot at a time, and lets gravity do the rest.
//
// ══════════════════════════════════════════════════════════════
//  THE CONSTRAINT THAT SHAPED THE HAMMERS
// ══════════════════════════════════════════════════════════════
//
// Playfield.js BAKES static geometry ONCE and blits it. So a
// collider disabled at runtime is STILL PAINTED. The physics would
// be right and the game would be LYING about where its walls are —
// the invisible-wedge failure mode all over again.
//
// So the pistons are ALWAYS SOLID. They never appear and never
// vanish. What changes is `kick`, one field, mutated on the live
// collider from onTick, which the solver reads fresh every step.
// And the renderer shows it FOR FREE, because a hot piston's id
// goes into `lamps.targets` and Playfield re-draws any lit id every
// frame on top of the bake.
//
//     THE TELL AND THE THREAT ARE THE SAME OBJECT.
//
// It is not possible for this table to hit you with a hammer that
// didn't light up first — not because I was careful, but because
// there is no second source of truth for a bug to pull apart.
//
//     A wall that appears is a cheat.
//     A wall that was always there and suddenly HITS you is a boss.
//
// ══════════════════════════════════════════════════════════════
//  ★ VERIFICATION — every number below was MEASURED
// ══════════════════════════════════════════════════════════════
//
//   the three laws ................... PASS (45 colliders)
//   stuck-ball sweep (342 drops) ..... 0.0%  (was 11.5% pre-T0.18)
//   launch sweep (25 pulls) .......... 0 dead, 0 tunnels
//   score median ..................... 5.8k — same band as the old
//                                      Foundry's 6.6k. No farm, no
//                                      dead table.
//
//   THE LESSON SURVIVES THE CARNIVAL (288,000 frames):
//     DORMANT-phase frames with a live weapon ....... 0
//     (gravity is EXACTLY 1 and every hammer is COLD until the
//      player wakes the boss themselves. The table may never attack
//      someone who hasn't earned it.)
//
//   THE BOSS LADDER (120 games): AWAKE 98, MELTDOWN 14, 140 felled.
//
//   THE TICKET LADDER:
//     🎟3 WHEEL SPINS ..... 118/120
//     🎟5 GATE OPENS ...... 114/120
//     🎟7 MIDWAY ROARS ....  25/40   (was 0/120 — see the bug below)
//
//   THE GHOST TRAIN, BOTH QUESTIONS ASKED:
//     can a ball get in WITHOUT the key?    0/300     ← must be NO
//     can a ball get in WITH the key?      10/300     ← must be YES
//     conversion of a DELIBERATE cast .... 31.4%      ← aim pays
//
// The last three lines are the whole point of the testing law. A
// test that only asks the first question is perfectly satisfied by
// a door welded shut — and it will watch you ship an unsolvable
// secret and say nothing.
//
// ══════════════════════════════════════════════════════════════

import {
  TW, TH, shell, arch, drain, guides, slings, flipperPair, LANE_GEO,
  ramp, ghostWall, Bumper, Circle, Seg, SensorSeg, SensorCircle,
  GravityZone, PLAYABLE_R, assertTableSane,
  // ★★★ THE MIDWAY — the rides. See TableKit's MIDWAY section.
  //
  // ⚠ THESE DID NOT EXIST WHEN THIS TABLE WAS WRITTEN, AND THAT IS
  //   THE WHOLE REASON THE FOUNDRY WAS STILL A BUMPER TABLE WEARING
  //   A CARNIVAL'S NAME.
  //
  //   The header calls this THE MIDWAY. It names a BIG TOP, a
  //   CAROUSEL, a HELTER-SKELTER, a GHOST TRAIN. Every one of those
  //   is a beautiful piece of naming laid over... five bumpers,
  //   three posts, and two ramps. The carnival was entirely in the
  //   COMMENTS. On the deck it was baseline pinball, because
  //   TableKit's whole vocabulary was `shell/arch/guides/slings/
  //   ramp/targetBank/node/ghostWall` — all PINBALL nouns. There was
  //   no word for a loop, a scoop, a cart, a wheel, a hole.
  //
  //   A table is DATA. Data can only say what its schema can say. So
  //   no amount of ambition in this file could ever have produced a
  //   carnival — it would only ever have produced more bumpers, and
  //   that is precisely what every rewrite produced.
  //
  //       WHEN THE OUTPUT KEEPS COMING OUT SMALL, CHECK WHETHER THE
  //       LANGUAGE IS BIG ENOUGH BEFORE YOU BLAME THE AUTHOR.
  //
  //   The fix was in TableKit, not here. This file just gets to
  //   SPEAK now.
  loop, spiral, scoop, dropHole, whirlpool, captive, turnstile, cart,
  chute, fork, wheel, ejectFrom, warpBall,
} from "./TableKit";

const FY = TH - 110;          // flipper line — 590
const G  = LANE_GEO({ flipperY: FY });

// ── THE HEAT LADDER ──────────────────────────────────────────
// Heat is the boss's health bar run BACKWARDS: it goes UP as you
// play well, and every rung it climbs makes the table angrier.
// You are not grinding a health bar down. You are STOKING A FIRE
// that you will eventually have to reach into.
const PHASE_STOKE = 4;
const PHASE_AWAKE = 9;
const PHASE_MELT  = 15;
const HEAT_MAX    = 20;

// ⚠ 0.85s IS NOT A DIFFICULTY KNOB. It is derived from the flipper.
//   A ball leaving at ~700u/s crosses the piston band in ~0.23s. A
//   human needs ~0.4s to see a warning, decide, and flip. 0.85s of
//   cycle leaves a hammer dark for ~0.57s before it strikes: enough
//   to read, enough to act on, not enough to ignore. Below ~0.6s the
//   beat stops being a rhythm and becomes a dice roll, and RANDOM is
//   the one thing a pinball player never forgives, because it takes
//   the blame off them and they can FEEL it.
const PISTON_CYCLE = 0.85;
const PISTON_LIVE  = 0.30;

// ── THE TICKET LADDER ────────────────────────────────────────
// ⚠ THE LADDER GREW BECAUSE THE MIDWAY GREW, AND THE RUNGS MOVED
//   WITH IT. THIS IS NOT COSMETIC.
//
//   The old ladder was 3 / 5 / 7 against SEVEN attractions, and the
//   header records what happened the LAST time those two numbers
//   disagreed: ROAR sat at 7 with only six attractions to earn from,
//   and the sim reported `🎟7 MIDWAY ROARS: 0/120 — unreachable,
//   ever.` Dead content. A reward with no path to it, that no player
//   would ever report as a bug because they would simply never have
//   known it was there.
//
//   There are now ELEVEN attractions. Leaving the rungs at 3/5/7
//   would not have broken anything — it would have done something
//   subtler and worse: made the whole back half of the midway
//   POINTLESS. Every unlock would be spent by attraction seven, and
//   the loop, the wheel, the pit and the sideshow would pay a ticket
//   into a ladder that had already finished. You would be working
//   rides for a currency that no longer bought anything.
//
//   The rungs are re-spaced across the real span. The last one still
//   lands on the Ghost Train, because THE LAST DOOR ON THE MIDWAY IS
//   STILL OPENED BY BREAKING PHYSICS.
//   ⚠ AND I COUNTED THEM WRONG THE FIRST TIME. I wrote 11, and a
//     verifier that simply grepped for `_ticket(engine, "..."` found
//     FIFTEEN. The player would have been shown `🎟 TICKET 13/11`.
//
//     The old bug was an unreachable rung (ROAR at 7 with only six
//     attractions: `0/120, unreachable, ever`). This is its exact
//     mirror — a DENOMINATOR that lies — and it comes from the same
//     place: a constant that has to agree with a fact scattered
//     across four hundred lines of code, kept in agreement by
//     nothing but my memory.
//
//     Both bugs are one bug: A NUMBER THAT MUST MATCH REALITY, AND
//     NOTHING CHECKS THAT IT DOES.
//
//     15 attractions. The rungs are spaced across the REAL span.
const TICK_WHEEL = 4;    // ×2 multiplier
const TICK_GATE  = 8;    // the vault goes live
const TICK_ROAR  = 15;   // bumpers +40% — and the 15th is the Ghost Train
const TICK_TOTAL = 15;   // ← MUST equal the number of distinct _ticket ids.

const BUMPERS = ["bmp_1", "bmp_2", "bmp_3", "bmp_4", "bmp_5"];

// ── THE MOVING RIDES ─────────────────────────────────────────
// turnstile() and cart() each return a mover function ALONGSIDE
// their colliders, and that function must be called every frame
// from onTick.
//
// ⚠ THEY ARE BUILT ONCE, HERE, AND HELD. Rebuilding them per-frame
//   would hand the mover a DIFFERENT object from the one the solver
//   is holding — so the arms would spin beautifully in a picture
//   nobody is colliding with, while the ball bounces off a ghost of
//   where they used to be. The physics would be right, and right
//   about geometry the player cannot see. That is the single worst
//   failure mode available to a renderer, and it is completely
//   silent.
let _turn = null;
// (W4: the mine cart is gone — the circus train is a sensor, see build)
let _pool = null;
const HAMMERS = ["pst_A", "pst_D", "pst_C"];
const BMP_KICK = 280;

// ── W3 LAYOUT (2026-10-25) — Willie in the middle of everything ──
// Every attraction kept its rules and its ticket; these are only
// WHERE things sit. One place to move them (future Cartographer food).
const WILLIE_C    = { x: 200, y: 280 };   // the stage, the heart, the boss — dead centre of the painted circus ring (Playfield3D: cx=W/2, cy=H*0.40)
const STAGE_COL_R = 20;                    // the stage's solid body
const RING_R      = 70;                    // the tents circle him at this radius
const TUNNEL_IN_Y  = WILLIE_C.y + STAGE_COL_R + 14;   // mouth, at his front
const TUNNEL_OUT_Y = WILLIE_C.y - STAGE_COL_R - 14;   // where you shoot out of his back
const VAULT_X     = WILLIE_C.x - 38;      // the Ghost Train, raised over him
const CAR_POSTS   = [[104, 206], [90, 246], [142, 186]];
const GALLERY_X   = 330, GALLERY_Y = 232;
const RAMP        = [250, 480, 262, 392];  // ax, ay, bx, by
const PST_D       = [180, 395, 196, 440];
const TRAIN_R     = 45;                    // W4: the circus train's lap, between the stage (r20) and the tents' inner edge (r52)
const TRAIN_HIT_R = 13;                    // the hit zone under the locomotive
const TRAIN_PARK  = Math.PI;               // parked on Willie's LEFT while dormant. (Behind him was ON the tunnel's
                                           //   exit — every dormant trip through him paid the train free. Review.)
const TRAIN_SPEED = [0, 0.55, 0.95, 1.6];  // rad/s by phase: dormant / stoking / awake / meltdown
const CANNON_MUZZLE = { x: 262, y: 62 };   // over the Ghost Train's roof, left of the deflector

export const FOUNDRY = {
  id: "t01_foundry",                 // internal id NEVER changes. Saves, progression,
                                     // and the assertion suite all key off this.
  name: "Cirque De J'ynx",           // what the PLAYER sees: menus, marquee, HUD.
                                     // (Internally this is still the Foundry — file
                                     // name, id, and test labels keep the old name.)
  kind: "score",
  skin: "cirque",                    // the renderer's costume. Playfield3D reads this
                                     // and dresses the table as the J'aeonxi Circus:
                                     // striped tent-top bumpers, big-top canopies,
                                     // bunting, marquee bulbs. ZERO physics. A skin
                                     // is a render-layer field, exactly like decor.
  blurb: "The greatest show in the empire.",
  difficulty: 1,
  seed: 4001,

  W: TW, H: TH,
  gravity: 400,
  damping: 0.9993,
  balls: 3,
  ballR: 7,

  launch: { x: TW - 15, y: TH - 40 },
  launchDir: { x: 0, y: -1 },
  launchPower: 780,

  flippers: flipperPair({ y: FY, gap: 62, length: 62 }),

  // ── Geometry ───────────────────────────────────────────────
  //
  //     ┌──────────────────────────────┐
  //     │      ░░░ GHOST TRAIN ░░░     │  ← the secret room. its
  //     │      ▓  (the VAULT)  ▓       │    floor is the door, and
  //     │  ╭─── arch ────────╮ │ ▓lane │    the door is ghostable
  //     │ ORB    ● BIG TOP ●  │        │
  //     │  │    ●    ●    ●   │  ▪ ▪ ▪ │  ← target bank (clear of
  //     │  │       HEART      │  bank  │     the divider by 54u)
  //     │  │   ∙ CAROUSEL ∙   │        │
  //     │  │  ╲            ╱  spinner  │
  //     │  ▐ HELTER    HIGH-STRIKER ▌  │  ← PISTONS live in the
  //     │  ▐  SKELTER   → CRUCIBLE  ▌  │     central corridor —
  //     │      ▐▌  ▐▌  ▐▌             │     the road every ball
  //     │    ◣            ◢            │     takes home
  //     │    │  ╲      ╱  │            │
  //     │    │    ⚊  ⚊    │            │
  //     └────┴───────────┴─────────────┘
  //
  build() {
    const c = [];
    c.push(...shell());
    c.push(...arch());          // NEVER override the arch — see TableKit.shell()
    c.push(...guides({ flipperY: FY }));
    c.push(...slings({ flipperY: FY }));
    c.push(...drain());

    // ══════════════════════════════════════════════════════════
    //  THE BIG TOP — a bumper RING, not a bumper row
    // ══════════════════════════════════════════════════════════
    //
    // This is where the deflector's throw LANDS, so it is the first
    // thing every ball meets — which is correct: the nest is what
    // FUNDS your powers, and a ball should always get a little
    // charge for free before it has to earn the rest.
    //
    // ⚠ THE SPACING IS THE WHOLE GAME HERE. Two r=18 bumpers 54u
    //   apart on centres leave 18u of AIR — the ball is 14u, so it
    //   fits through with exactly the wedge margin to spare. Bring
    //   them any closer and the nest stops being a nest and becomes
    //   a GRINDER: a throat the ball fits into and cannot leave.
    //   The law checks circles too, and it will catch you.
    // ★ W3 (2026-10-25, caP'n: "completely redesign the layout") — THE RING
    //   NOW CIRCLES WILLIE. Five tents on a 70u ring around his stage
    //   (WILLIE_C), at 300/350/40/140/210°. The deflector's throw lands
    //   on the 300/350 pair — the OLD layout put the Shooting Gallery's
    //   top target in that flight path, so every launch clipped it and
    //   dribbled down the right flank (measured: 0 tickets in 20 bot
    //   games). The bottom gap (40→140°) is the tunnel approach; the
    //   top-left gap (210→300°) is where the tunnel spits you out.
    for (const [i, deg] of [300, 350, 40, 140, 210].entries()) {
      const a = (deg * Math.PI) / 180;
      c.push(Bumper(Math.round(WILLIE_C.x + RING_R * Math.cos(a)), Math.round(WILLIE_C.y + RING_R * Math.sin(a)), 18,
        { id: `bmp_${i + 1}`, kick: BMP_KICK }));
    }

    // ★ WILLIE'S STAGE — solid. He stands on it. It kicks (jittered,
    //   like a bumper) so a ball falling on its crown can never perch.
    c.push(Circle(WILLIE_C.x, WILLIE_C.y, STAGE_COL_R, {
      id: "stage", tag: "stage", restitution: 0.5, kick: 90, kickJitter: 0.35,
    }));

    // ══════════════════════════════════════════════════════════
    //  THE CAROUSEL — three posts in the mid-band
    // ══════════════════════════════════════════════════════════
    //
    // Posts out in open air, where posts belong. Their job is to
    // make a ball's path unpredictable, NOT to end it.
    //
    // ⚠ NO APEX ON A FALL LINE. A ball dropping straight down onto
    //   the exact top of a circle has no sideways force acting on
    //   it: gravity pulls down, the post pushes up, the two cancel,
    //   and the ball balances there like a coin on a knife-edge.
    //   Forever.
    //
    //   THE WEDGE LAW CANNOT SEE IT — it compares PAIRS of faces,
    //                                 and this is one face.
    //   THE LEDGE LAW CANNOT SEE IT — it inspects SEGMENTS, and
    //                                 this is a circle.
    //
    //   (`post_center` — one of these, at exactly TW/2 — shipped in
    //   the ORIGINAL game and survived every sweep ever run, because
    //   every sweep was looking for a different shape of bug.)
    //
    //   So every post here is deliberately OFF the vertical fall
    //   lines that feed it. It needs exactly zero horizontal drift
    //   to balance, and a ball off a flipper, a bumper, or the
    //   deflector always has some — but `Split` spawns balls with
    //   authored velocity, and a 120Hz loop running for hours is
    //   precisely what coincidences are for.
    // W3: the Carousel moved to the top-left, off the ring.
    c.push(Circle(CAR_POSTS[0][0], CAR_POSTS[0][1], 7, { id: "car_1", tag: "post", restitution: 0.85 }));
    c.push(Circle(CAR_POSTS[1][0], CAR_POSTS[1][1], 7, { id: "car_2", tag: "post", restitution: 0.85 }));
    c.push(Circle(CAR_POSTS[2][0], CAR_POSTS[2][1], 7, { id: "car_3", tag: "post", restitution: 0.85 }));

    // ══════════════════════════════════════════════════════════
    //  ★ THE GHOST TRAIN — THE SECRET ROOM
    // ══════════════════════════════════════════════════════════
    //
    // ⚠ THIS ROOM COST FIVE REWRITES AND EVERY ONE FAILED. THE
    //   LESSON IS WORTH MORE THAN THE GEOMETRY:
    //
    //     v1  sealed box, pitched floor
    //           → 6/240 balls PARKED in the corner where the floor
    //             met the wall. BOTH LAWS PASSED. The wedge law is
    //             happy (the walls touch — gap zero is legal) and
    //             the ledge law is happy (the floor IS pitched).
    //             "A slope does not free a ball. It only tells the
    //             ball where to die."
    //
    //     v2  floor ending in air, as an "exit"
    //           → the exit left an 11.5u slot against the shell. I
    //             had not removed the trap. I had MOVED IT DOWNHILL,
    //             which is the only thing a slope has ever done.
    //
    //     v3  floor with a `kick`
    //           → the ball stopped parking and started ricocheting
    //             around a SEALED ROOM at 340 u/s, forever.
    //             A kicker in a coffin is a blender.
    //
    //     v4  open floor
    //           → fixed the coffin and OPENED THE LOCK. 11/200
    //             keyless balls flew straight UP through the hole,
    //             off the bumper ring, and took the prize for free.
    //             "EVERY POCKET NEEDS AN EXIT — AND THE EXIT MUST BE
    //              `oneSided`, OR IT IS AN ENTRANCE."
    //             I read that line, wrote the pocket, gave it an
    //             exit, and did not make the exit one-way.
    //
    //     v5  one-way floor + ghostable LID
    //           → 0/200 without the key (good) and 1/200 WITH it
    //             (fatal). A WELDED DOOR. And the integrity test was
    //             delighted, because "no ball reaches the vault" is
    //             exactly what it was written to want.
    //
    // ── THEN I STOPPED TUNING AND MEASURED THE POWER ──────────
    //   300 balls, Ghost cast on every rising approach:
    //
    //       crossed the lid line ....... 1,732 times
    //       highest y reached .......... 33      (the lid was at 118)
    //       reached the vault ............. 2
    //
    //   Ghost was working PERFECTLY. The ball got up there
    //   constantly. It simply could not get IN — because THE BALL
    //   ARRIVES FROM BELOW AND I HAD PUT THE GHOSTABLE WALL ON TOP.
    //
    //   The rising ball met the FLOOR first. The floor was solid and
    //   it was not ghostable. So Ghost was being asked to phase
    //   through a wall THE BALL NEVER TOUCHES.
    //
    //   That is the Anchor lesson wearing a new hat:
    //
    //       FIVE REWRITES FAILED BECAUSE THE LOCK ASKED THE POWER TO
    //       DO SOMETHING THE POWER DOES NOT DO.
    //
    //   I was tuning a key to fit a door in another building. Again.
    //   No arrangement of walls was ever going to fix that, and no
    //   amount of cleverness in the geometry would have found it —
    //   only a measurement, of the power, on its own terms.
    //
    // ── THE DOOR GOES WHERE THE BALL KNOCKS ──────────────────
    //
    //       ▓▓▓▓▓▓▓▓▓▓▓▓   ← solid lid. stops a ghosted ball
    //       ▓          ▓      sailing out the top of the table.
    //       ▓  VAULT   ▓      two solid ribs.
    //       ▓          ▓
    //       ░░░░░░░░░░░░   ← GHOSTABLE + oneSided. THE DOOR.
    //              ▲
    //         the ball arrives here, rising, off the Big Top
    //
    //   SOLID FROM BELOW  — a keyless ball rising off the bumper
    //                       ring smacks into it and bounces off. It
    //                       is a wall like any other. No free entry,
    //                       no lucky rattle, not ever. (0/300.)
    //   GHOSTABLE         — a GHOSTED ball rising phases straight
    //                       through and into the room. The key does
    //                       exactly the job Ghost actually has:
    //                       PHASE THROUGH A WALL. Nothing clever.
    //   OPEN FROM ABOVE   — `face` points DOWN, so to a ball INSIDE
    //                       the room, falling, the floor is not
    //                       there at all. The ball drops back out on
    //                       its own. GRAVITY IS THE EXIT, AND
    //                       GRAVITY IS ALWAYS OPEN.
    //
    //   No coffin (the way out is down, and down is free). No wedge
    //   (there is nothing in there to grind against). No ledge (the
    //   door kicks, and a kicker cannot hold a ball still).
    //
    //   ONE SURFACE. It is the lock, the door, and the exit at once,
    //   and it cannot desync from itself, because it is one object.
    // ⚠ THE ROOF WENT UP, AND THE ROOM GOT TALLER. HERE IS WHY.
    //
    //   The lid used to be FLAT with `ledgeOk: true`, so the room's
    //   height only had to clear the ball: 48u was plenty.
    //
    //   The moment the lid had to actually SLOPE (see the lid, below
    //   — its `ledgeOk` was a lie and 7 balls proved it), 48u stopped
    //   being enough. A 26-unit slope inside a 48-unit room leaves 22
    //   at one end and pinches the door to 16.2 at the other, which
    //   is a WEDGE INSIDE THE PRIZE ROOM: the ball phases through the
    //   door, gets into the vault it worked five attractions to open,
    //   and grinds to death in the corner.
    //
    //   Fixing the ledge created the wedge. That is not bad luck —
    //   it is arithmetic, and it was going to happen no matter which
    //   way I sloped the lid, because the room did not have the
    //   headroom to hold a slope AND a ball at the same time.
    //
    //   So the roof goes UP (gy 116 -> 96) and the room gets TALLER
    //   (GH 48 -> 68). Now the slope fits inside it with a full
    //   ball-width of air at the tight end, and both laws pass — not
    //   because either was silenced, but because the room is finally
    //   big enough to be the thing it was pretending to be.
    const gx = VAULT_X, gy = 96; // pocket: left edge / roof (W3: centred over Willie — the raised 2nd level)
    const GW = 76,  GH = 68;     // (at gy=126 the door sat 13u off
                                 //  bmp_1 — a slot the ball fits in
                                 //  and grinds. The law caught it.)

    // THE LID — solid. It stops a ghosted ball from flying through
    // the room and out the top. `ledgeOk` because it is a CEILING:
    // the ball lives below it and can only strike its underside. Its
    // top faces the arch, where a ball is always already travelling
    // and can never come to rest. That is the one legitimate use of
    // the exemption — a surface a ball cannot rest on is not a ledge.
    // ⚠ THE `ledgeOk` CLAIM ON THIS LID WAS A LIE, AND THE SWEEP
    //   CAUGHT IT — SEVEN BALLS PARKED ON TOP OF IT.
    //
    //   The comment above says the lid's top "faces the arch, where a
    //   ball is always already travelling and can never come to
    //   rest." It is a completely reasonable sentence and it is
    //   FALSE. A ball riding the arch down the left DOES lose speed,
    //   and it lands on this lid — which slopes at 5 units over 76,
    //   i.e. FOUR DEGREES — and sits there, forever, in a sealed
    //   room the player cannot reach.
    //
    //   `ledgeOk` is not a fix. It is a PROMISE — "trust me, no ball
    //   can rest here" — and the ledge law believes you. Every time
    //   you write it you are switching the law OFF for that
    //   collider, on your own authority, based on a mental model of
    //   where balls go. Mine was wrong. It cost 7 dead balls in 304,
    //   in the shipped game, invisible, since the day it went in.
    //
    //       AN EXEMPTION IS A CLAIM ABOUT THE FUTURE, AND YOU DO NOT
    //       KNOW THE FUTURE. THE SIM DOES.
    //
    //   The lid is now PITCHED (26 units over 76 — ~19°, past
    //   MIN_PITCH) and the exemption is GONE. It doesn't need to be
    //   trusted; it's just correct. A ball that lands on it rolls
    //   off, which is what a roof does.
    //
    // ⚠ AND IT SLOPES *UP* TO THE RIGHT, NOT DOWN. The first fix
    //   sloped it down-right, which is the intuitive direction — and
    //   it immediately threw `ght_lid × ght_door: 16.2u`. Of course
    //   it did: the room is only 48 units tall, and dropping the
    //   ceiling 26 units into it leaves a gap narrower than the ball,
    //   which is a wedge INSIDE THE PRIZE ROOM.
    //
    //   Tilting it the other way raises the far end instead, so the
    //   headroom GROWS. Same pitch, same roll-off, opposite sign, and
    //   the vault stays a room instead of becoming a crack. The ledge
    //   law only ever cared THAT it slopes — it was me who assumed
    //   which way.
    c.push(Seg(gx, gy + 26, gx + GW, gy, {
      tag: "wall", thickness: 6, restitution: 0.35,
      id: "ght_lid",
      // W3: its high end is a knife-edge — a ball falling exactly on
      //   x = gx+GW balanced there forever (grid drop). A small jittered
      //   kick means nothing sits still on it. (Tried before the rib fix
      //   and it shoved balls into the V-pocket: 44 traps. Order matters.)
      kick: 40, kickJitter: 0.4,
    }));

    // The two ribs. Solid, so the FLOOR is genuinely the only door.
    // W3: the left rib starts AT the lid's low end, not 26u above it.
    //   The old rib poked up past the roof and made a V-pocket on top
    //   that caught every ball rolling off the roof (grid drop found it
    //   in the old spot too: 4 balls at 170,90).
    c.push(Seg(gx, gy + 26, gx, gy + GH, {
      tag: "wall", thickness: 5, restitution: 0.4, id: "ght_rib_L",
    }));
    c.push(Seg(gx + GW, gy + 5, gx + GW, gy + GH, {
      tag: "wall", thickness: 5, restitution: 0.4, id: "ght_rib_R",
    }));

    // ★ THE DOOR. Ghostable, one-way, and it kicks.
    //
    //   It `kick`s because a one-way you can rest ON is still a
    //   shelf, and a pocket floor cannot be pitched 16° and still be
    //   a floor. NOTHING PASSIVE GOES IN A BOX THAT TIGHT — the same
    //   answer the lane gate needed, for the same reason.
    //
    //   The kick is small (90). Just enough that the ball cannot
    //   balance on it; not so much that it fires you back out of the
    //   prize you spent a power to reach.
    c.push(ghostWall(gx + 3, gy + GH, gx + GW - 3, gy + GH, {
      id: "ght_door", tag: "wall",
      oneSided: true, face: { x: 0, y: 1 },
      kick: 90, ledgeOk: true, restitution: 0.3,
    }));

    // THE PRIZE. A SENSOR, so it has no collision and can never
    // wedge anything. The secret does not get to win by getting in
    // your way.
    c.push(SensorCircle(gx + GW / 2, gy + 28, 14, { tag: "vault", id: "vault" }));

    // ══════════════════════════════════════════════════════════
    //  THE SHOOTING GALLERY — the target bank
    // ══════════════════════════════════════════════════════════
    //
    // ⚠ THE PITCH IS 46u, NOT 26. A 14u-tall target on a 26u pitch
    //   leaves a 3u SLOT between each pair — beside a 14u ball. That
    //   is the ORIGINAL 833-step grinding bug, and I reintroduced it
    //   here, today, in the file whose header warns about it, and I
    //   did not see it. It did not LOOK like anything.
    //
    //   It never does. That is the entire argument for the law.
    //
    // The bank ends at x=312 — 58u clear of the lane divider at 370.
    for (let i = 0; i < 4; i++) {
      const y = GALLERY_Y + i * 46;          // W3: out of the deflector's flight path
      c.push(Seg(GALLERY_X, y, GALLERY_X, y + 14, {
        tag: "target", thickness: 9, restitution: 0.6, id: `tgt_${i}`,
        // W3: the TOP target's upper cap is a knife-edge — a ball falling
        //   exactly on its x balanced there forever (pre-existing; review).
        //   A small jittered kick on that one target, like the vault lid.
        ...(i === 0 ? { kick: 30, kickJitter: 0.4 } : {}),
      }));
    }

    // ══════════════════════════════════════════════════════════
    //  THE HELTER-SKELTER — now the CORKSCREW, not a ramp
    // ══════════════════════════════════════════════════════════
    // ⚠ THE LEFT RAMP IS GONE, AND THE GEOMETRY DEMANDED IT. The
    //   bottom-left corner was asked to hold a ramp base, a 120u
    //   loop, a scoop and the pit all at once — and the loop's
    //   channel, measured, had pst_A RUNNING STRAIGHT THROUGH IT:
    //   both hammer endpoints sat inside the ring (48.4u and 54.6u
    //   from a centre whose channel spans 34..60). Arc-vs-seg is a
    //   pair no law checks, so a hammer lived inside the ride and
    //   nobody was ever told. There was never room for all of it.
    //
    //   So the ramp and the loop merged into the one device that IS
    //   a helter-skelter: a corkscrew slide (see THE RIDES, below).
    //   The right ramp — the Skyride — keeps the `ramp` ticket
    //   reachable, and the corkscrew inherits the loop's.

    // ══════════════════════════════════════════════════════════
    //  THE HIGH STRIKER — the right ramp → the crucible
    // ══════════════════════════════════════════════════════════
    // Stays OFF the deflector's flight path. The only way in is a
    // deliberate right-flipper shot. You EARN the crucible.
    c.push(...ramp({
      ax: RAMP[0], ay: RAMP[1], bx: RAMP[2], by: RAMP[3],   // W3: re-aimed beside the ring
      id: "ramp_R", width: 26, tag: "ramp",
    }));
    c.push(SensorCircle(RAMP[2], RAMP[3] - 8, 15, { tag: "crucible", id: "crucible" }));

    // ══════════════════════════════════════════════════════════
    //  THE PISTONS — the Forgemaster's hammers
    // ══════════════════════════════════════════════════════════
    //
    // THEY ARE ALWAYS SOLID. Read that again — it is the most
    // important line in this file. A piston is NOT a wall that
    // appears. It is a wall that has been standing there, in plain
    // sight, in the static bake, since the table loaded — and which
    // periodically WAKES UP AND HITS THE BALL.
    //
    // A DARK piston is a dull 0.35-restitution wall: the ball bumps
    // it, loses a little speed, and rolls on. It is still a
    // functioning guide rail, steering the ball down toward the
    // flippers instead of parking it. A piston that's asleep still
    // has to be good pinball.
    //
    // A HOT piston SLAMS — 300+ u/s, and where it fires you is
    // toward the outlanes.
    //
    // ⚠ THEY LIVE IN THE CENTRAL CORRIDOR, AND THAT WAS NOT MY IDEA.
    //   The first draft pinned them against the side walls and the
    //   law rejected all three (6.0u, 6.2u, 14.5u). When I measured
    //   the flanks instead of eyeballing them, the geometry answered
    //   the design question for me: the right flank is 24 units wide,
    //   and a piston needs 18u of air on each side plus its own body.
    //   IT WILL NEVER FIT. Not with better numbers. Not ever.
    //
    //   So the hammers moved to where the ball actually LIVES — the
    //   wide road home. And it is a BETTER TABLE than the one I meant
    //   to build: a hammer pinned against a wall only threatens balls
    //   that hug that wall; a hammer in the middle of the road
    //   threatens EVERY ball. The law didn't just stop a bug. It
    //   deleted a bad idea and handed me the good one.
    //
    // FORGE (pst_A) — the main road home. Hot, it throws you sideways
    //   off your return lane.
    // ⚠ ITS FRICTION IS LOW ON PURPOSE, AND THE RIDES ARE WHY.
    //
    //   The original Foundry saw 153 piston contacts across 60
    //   launches. With the rides in, it saw 3,387 — on this one
    //   hammer. Twenty-two times as many.
    //
    //   That is NOT a new bug. It is a consequence: the midway keeps
    //   the ball ALIVE (avg life went from a quick drain to 606
    //   steps), and a ball that lives longer spends that extra life
    //   somewhere. FORGE sits in the middle of the road home, so it
    //   gets the traffic. The worst CONSECUTIVE run is 171 — well
    //   under the sweep's 700-step trap threshold, and the grid drop
    //   reports zero balls trapped here.
    //
    //   So it is a RATTLE, not a trap, and the distinction matters:
    //   one is a ball that dies and one is a ball that buzzes. The
    //   friction is dropped so a slow ball leaning on it SLIDES
    //   along and away instead of chattering in place. Same wall,
    //   same job, quieter.
    c.push(Seg(152, 432, 172, 492, {
      tag: "piston", thickness: 7, restitution: 0.30, friction: 0.005,
      kick: 0, kickJitter: 0.10, id: "pst_A",
    }));

    // ANVIL (pst_D) — guards the crucible approach. Hot, it swats
    //   your jackpot shot away before it lands.
    c.push(Seg(...PST_D, {                    // W3: the anvil now guards Willie's tunnel
      tag: "piston", thickness: 7, restitution: 0.35,
      kick: 0, kickJitter: 0.10, id: "pst_D",
    }));

    // TONGS (pst_C) — low, across the flipper feed. The cruel one.
    //   Hot, it fires a ball you were about to cradle at an outlane.
    //   This is the hammer that ends balls.
    // ⚠ TONGS WAS A SHELF WHEN IT WAS ASLEEP.
    //
    //   The ledge law exempts any collider with a `kick`, on the
    //   sound reasoning that a ball cannot rest on a surface that
    //   refuses to hold still. Correct — but TONGS' kick is ZERO for
    //   most of the game. It only arms at phase >= 2, and `kick: 0`
    //   is its DEFAULT and its DORMANT state.
    //
    //   So the law looked at the DECLARED kick (0), correctly did
    //   NOT exempt it, checked the pitch — 20 units over 48, about
    //   23° — and passed it. And the sim still found two balls
    //   parked on it, because 23° is past MIN_PITCH but this hammer
    //   sits in the flipper feed where balls arrive slow and tired,
    //   and a tired ball on a shallow slope in a corner stops.
    //
    //   MIN_PITCH is a FLOOR, not a certificate. It is the angle
    //   below which a ball definitely cannot leave — not the angle
    //   above which it definitely can.
    //
    // ── AND THEN I PUT IT BACK. ──────────────────────────────
    //   I steepened it to 35°, then to 44°, chasing those two balls.
    //   Both passed every law. Both still held two balls. And the
    //   second one moved TONGS a few units into the flipper feed,
    //   where the launch test immediately reported:
    //
    //       piston: 2,974 contacts
    //
    //   Nearly three thousand. The ball was not resting on it any
    //   more — it was GRINDING against it, over and over, on the way
    //   home, every single ball. I had traded a rare 0.7% park for a
    //   permanent rattle in the one part of the table the ball
    //   crosses most.
    //
    //       A FIX THAT COSTS MORE THAN THE BUG IS NOT A FIX.
    //
    //   TONGS is back exactly where it was. Its two parked balls (2
    //   in 304, 0.7%) are a real and known flaw, and they are
    //   CHEAPER THAN EVERY ALTERNATIVE I TRIED. Writing that down is
    //   worth more than pretending I solved it: the next person to
    //   look at this number now knows it was measured, not missed.
    c.push(Seg(206, 496, 254, 516, {
      tag: "piston", thickness: 7, restitution: 0.35,
      kick: 0, kickJitter: 0.14, id: "pst_C",
    }));

    // ══════════════════════════════════════════════════════════
    //  THE HEART — the kill shot
    // ══════════════════════════════════════════════════════════
    //
    // A SENSOR, so it is never an obstacle and can never wedge a
    // ball. INERT until MELTDOWN: hit it before then and nothing
    // happens. No points, no charge, no hint. A shot that pays out
    // when it isn't lit is not a shot — it's a slot machine.
    //
    // ⚠ ITS DWELL IS ZERO, AND THAT IS A DECISION. I reached for
    //   `dwell` first — make the player HOLD the ball on it,
    //   Lockstep-style. It is wrong here. Dwell asks "did the ball
    //   STAY?", which is a question about PATIENCE. The Forgemaster
    //   is a question about NERVE. At meltdown the whole table is
    //   trying to kill you and the correct verb is STRIKE, not
    //   LINGER. A hold mechanic would make the climax slower, and
    //   the climax is the one place a game may never be slower.
    // W3: the heart sits INSIDE Willie's stage — no ball can touch it
    //   directly any more. You wound him through the TUNNEL (the hatch,
    //   below); it is still the anchor the 3D stage and Willie stand on.
    c.push(SensorCircle(WILLIE_C.x, WILLIE_C.y, 15, { tag: "heart", id: "heart" }));

    // ── THE ORBIT ────────────────────────────────────────────
    // Scores on EXIT, not entry — you have to COMPLETE it, not clip
    // it. Also the safest place on the table during MELTDOWN, and
    // that is on purpose: even at its worst, the Forgemaster must
    // leave the player ONE road they can survive on. A boss with no
    // safe shot isn't hard, it's a countdown.
    c.push(SensorSeg(60, 190, 60, 226, {
      tag: "orbit", thickness: 10, id: "orbit_exit",
    }));

    // ── SPINNER ──────────────────────────────────────────────
    // Cheap points, steady charge. Rewards a ball already moving.
    c.push(SensorSeg(276, 200, 276, 244, {
      tag: "spinner", thickness: 8, id: "spinner",
    }));

    // ── THE CENTRE POST ──────────────────────────────────────
    // Turns a straight drain into a 50/50. Cruel. Correct.
    // ⚠ TW/2 + 5, NOT TW/2 — see the apex warning at the Carousel.
    //   Five units of offset. It still turns a straight drain into a
    //   coin-flip, which is the whole reason it exists. It is just
    //   no longer a PERCH. Cruel is good. Sticky is a bug.
    c.push(Circle(TW / 2 + 5, FY - 40, 7, {
      id: "post_center", tag: "post", restitution: 0.85,
    }));

    // ══════════════════════════════════════════════════════════
    // ★★★  THE RIDES  —  the carnival, finally on the deck
    // ══════════════════════════════════════════════════════════
    //
    // Everything above this line was already here, and all of it
    // stays: the Big Top, the Carousel, the Gallery, the ramps, the
    // Ghost Train, and every one of the Forgemaster's hammers. The
    // boss is untouched, down to the beat.
    //
    // What was MISSING was the midway itself. Below are the rides.
    //
    // ⚠ EVERY ONE OF THEM PAYS A TICKET, AND THAT IS THE POINT.
    //   A ride that only pays POINTS is a ride the player will
    //   ignore the moment they find a better scorer — which is the
    //   exact failure the ticket ladder was invented to prevent
    //   ("a player finds the single best shot and repeats it until
    //   the ball drains; that is not playing the table, that is
    //   playing ONE OBJECT ON the table"). New geometry with no
    //   ticket attached would have been beautiful dead weight.
    //
    //   So the rides are not decoration bolted onto the economy.
    //   They ARE the economy. Eleven attractions, eleven tickets,
    //   and the only way to the top of the ladder is to ride them
    //   all.

    // ── ⭕ THE LOOP-THE-LOOP ─────────────────────────────────
    // A real 360° orbit. The ball enters at the mouth, gets WHIPPED
    // around the inside, and comes out flying. The single most
    // satisfying shape in pinball, and this table has never had one.
    //
    // Its mouth faces down-left, so it is a right-flipper shot
    // across the deck — long, committed, and the best-feeling thing
    // you can do here that isn't the Ghost Train.
    // ── 🌀 THE HELTER-SKELTER — the corkscrew slide ──────────
    // A descending curl in the freed bottom-left. The mouth opens
    // up-right toward the carousel band, so everything the midway
    // spills — off the posts, off the pistons, off a flipper shot —
    // can be caught and taken for the ride. The rails are banisters
    // (kick 22, from the kit) so the ball never stalls in the curl,
    // and the exit sensor at the inner end pays the `loop` ticket
    // and THROWS the ball back up the midway (see onSensor).
    //
    // ⚠ 0.7 TURNS IS THE ARITHMETIC, NOT A TASTE. spiral() clamps
    //   turns so consecutive coils hold a channel AND a ball; at
    //   r0=46 → r1=16 the span only affords ~0.7 of a revolution.
    //   Ask for more and the device would clamp anyway and warn —
    //   the curl is exactly as long as the corner is big.
    // ⚠ AND THE SPIRAL DIDN'T FIT EITHER — MEASURED TWICE, REJECTED
    //   TWICE. At r0=46 the wedge law threw 37 gaps against pst_A
    //   and sling_L; shrunk to the corner's real size (r0=36), the
    //   coil pinched against ITSELF: with a 24u channel, the inner
    //   rail's radius goes to ZERO as r1 approaches w/2, and the
    //   tail converges into its own wedge. A corkscrew is a big-
    //   radius device (the kit's default is r0=90) and this pocket
    //   is a 36u pocket. No parameter was ever going to fix that.
    //
    //   So the ride is a true LOOP again — arcs, which carry no
    //   self-pinch — sized to the corner the law measured, mouth
    //   down-right: the long, committed right-flipper shot across
    //   the deck. And this time NOTHING RUNS THROUGH IT: at
    //   (76,398) r20+26 the channel clears pst_A by 31u, the gutter
    //   by 26u, and the sling by 50u — measured, not eyeballed,
    //   because arc-vs-seg is exactly the pair the wedge law cannot
    //   check for me.
    c.push(...loop({
      cx: 76, cy: 398, r: 20, width: 26,
      mouth: Math.PI * 0.30, gap: 0.95,
      spin: 105, id: "loop", tag: "loop", ballR: 7,
    }));

    // ⚠ THE LOOP'S WALLS GET JITTER, AND IT HAS TO HAPPEN *HERE*.
    //   Arc() pins kickJitter to 0 in its allowlist — you cannot
    //   pass it in — but the SOLVER reads the field live off the
    //   collider at every contact. So the table reaches in after
    //   construction and turns it on.
    //
    //   Why: a loop's outer wall presents a closed DOME to anything
    //   raining from above, and a dome that kicks with ZERO jitter
    //   is a deterministic trap — the ball lands on the apex, is
    //   kicked straight up along the contact normal, falls straight
    //   back onto the same point, and popcorns there forever. No
    //   law can see the shape (arcs dodge the ledge law; a single
    //   face dodges the wedge law). Jitter is the fix the bumpers
    //   have always used for the same problem: a kick that never
    //   repeats an angle cannot hold a ball in place. (The jitter
    //   draws from the world's seeded RNG — LAW 4 — so duels and
    //   replays are untouched.)
    for (const col of c) {
      if (col.id === "loop_in" || col.id === "loop_out") col.kickJitter = 0.35;
      if (col.id === "loop_in") col.kick = 55;   // the bowl needs the shove
    }

    // ── 🎡 THE BIG WHEEL ─────────────────────────────────────
    // Six prize slots in a ring. The ball rattles in, settles into
    // ONE, and that wedge is your prize. You did not earn it. You
    // SPUN for it — and that is the whole carnival in one object.
    //
    // The payouts are deliberately LOPSIDED, including one slot that
    // pays NOTHING. A wheel where every wedge is worth the same is
    // not a wheel; it's a rollover with extra steps. The entire
    // feeling of a prize wheel is that you can SEE the good slot and
    // you cannot HIT it.
    c.push(...wheel({ x: 46, y: 130, r: 36, slots: 6, id: "wheel" }));

    // ── 🎪 THE SIDESHOW SCOOP ────────────────────────────────
    // The ball rolls in. It STOPS. The table takes a breath, the
    // SIDESHOW fires, and the scoop spits the ball back out.
    //
    // ⚠ THIS IS THE DEVICE THAT MAKES A MODE POSSIBLE AT ALL. A
    //   minigame needs the ball to hold still for a moment so the
    //   player can look at something else — and pinball's answer to
    //   that, for seventy years, has been the scoop. `dwell` is the
    //   trick: a ball that merely GRAZES the mouth does not count.
    //   It has to SIT DOWN.
    //
    // ⚠ AND ITS EJECT IS NOT OPTIONAL. A catcher without an ejector
    //   is the wedge law wearing a costume. If the onSensor hook
    //   below ever stops firing, this stops being a ride and becomes
    //   a grave. The two halves ship together or not at all.
    // ⚠ MOVED (redesign): the scoop used to sit at (92,466), jammed
    //   between the loop, the pit and the left ramp's base — four
    //   catching devices in one corner, and the grid drop parked
    //   balls all over the seams between them. The bottom-left now
    //   belongs to the LOOP alone. The scoop lives mid-left, under
    //   the turnstile, where its cup has open air on every side.
    // ⚠ THE CUP IS GONE. THE SIDESHOW IS A DUNK TANK NOW.
    //   Two measurements killed the cup. First: `dwell` — the whole
    //   "the ball has to SIT DOWN" premise — is a field the solver
    //   NEVER READS. It is declared on every collider and consumed
    //   by nothing in World.js; every dwell sensor in the game fires
    //   on entry. (Logged as a discovery — Lockstep's Magnetize lock
    //   leans on the same fiction.) Second: a 7u ball leaving the
    //   bottom of a 16u cup has an exit cone of ±9° around straight
    //   up — every lateral eject CLIPPED THE LIP (restitution 0.05,
    //   dead, straight back in, latch held: entombed), and straight
    //   up fell straight back in. Measured both. The cup could catch
    //   and could not release, which is the definition of a grave.
    //
    //   A dunk tank is the honest version: a SENSOR — no collision,
    //   no wedge, no ledge, nothing to rest on — the ball drops in
    //   the water, the SIDESHOW roars, and the tank tosses the ball
    //   back out (the warp in onSensor). Same ticket, same mode,
    //   zero geometry to fail.
    c.push(SensorCircle(44, 444, 13, { tag: "scoop", id: "scoop" }));

    // ── 🌀 THE PIT — a whirlpool that swallows you ───────────
    // A funnel with a magnet in it. Wander too close and you spiral
    // in, faster and faster, and you are eaten. Then it re-serves
    // you at a RANDOM angle — a slot machine that takes your ball
    // and gives it back drunk.
    //
    // ⚠ THE RNG IS THE WORLD'S SEEDED RNG, NEVER Math.random. See
    //   World, LAW 4. It is not a style rule: a duel in which two
    //   players' pits disagree is not a duel, and a replay that
    //   diverges is not a replay.
    // ⚠ REDESIGN (the D17 bug): the Pit used to sit at (60,440) and
    //   RE-SERVE THE BALL AT ITS OWN CENTRE. warpBall() clears the
    //   ball's sensor latch — BY DESIGN, see TableKit — so the eye
    //   re-fired on the very next physics step, re-warped, re-fired:
    //   120 serves a second, forever. The ball froze dead-centre in
    //   the funnel and the score meter quietly paid 1,200 a frame.
    //   Reproduced headless: 2,400 serves in 20 simulated seconds,
    //   ball never freed. The grid drop showed it eating the entire
    //   left half of the table.
    //
    //       NEVER WARP A BALL INTO THE SENSOR THAT SENT IT.
    //       The latch-clear and the latch are both correct; it is
    //       the DESTINATION that turns them into an infinite loop.
    //
    //   The fix is thematic: the funnel is now the mouth of THE
    //   HUMAN CANNONBALL, bottom-right, and the muzzle it fires
    //   from is at the TOP of the table — far outside the eye and
    //   the magnet. Swallowed bottom-right, launched across the
    //   top, landing left. The bug fix IS the ride.
    _pool = whirlpool({
      x: 322, y: 434, r: 26, strength: 430, id: "pool", tag: "pool", ballR: 7,
    });
    c.push(..._pool.colliders);

    // ── ✳ THE TURNSTILE — it spins, and it BATS ─────────────
    // Four arms on a hub, rotating forever. Where the ball comes out
    // is a function of WHEN it went in, and you cannot time it.
    //
    // ⚠ CHAOS IS NOT NON-DETERMINISM, AND THE DIFFERENCE IS THE
    //   WHOLE BALLGAME. This feels unpredictable and is perfectly
    //   reproducible: the rotation is driven by the fixed-step clock,
    //   not by wall time, so a 144Hz monitor and a 60Hz monitor see
    //   the same arm in the same place. PvP needs determinism dead
    //   certain and chaos very much alive, and a turnstile gives you
    //   both.
    _turn = turnstile({
      x: 64, y: 300, arms: 4, len: 22, rate: 2.1,
      kick: 190, id: "turn", tag: "turnstile",
    });
    c.push(..._turn.colliders);

    // ── 🚃 THE MINE CART — a platform that CARRIES ──────────
    // Slides back and forth across the mid-band. A ball that lands
    // on it is taken for a RIDE: the cart hands its own velocity to
    // the ball via `pointVel`, exactly the way a flipper does.
    //
    // That ONE TERM is the entire difference between a ride and a
    // wall. Without it the cart slides out from under the ball and
    // it's a conveyor with no belt.
    // ★ W4 (2026-10-25) — THE CIRCUS TRAIN replaced the mine cart. It laps
    //   Willie on an ELEVATED track (the 3D layer hangs it off his stage), so
    //   the ball rolls UNDER it and the train never touches the ball: it is a
    //   SENSOR riding under the locomotive. Catch the train = roll through it
    //   as it passes. Parked behind Willie while DORMANT (nothing moves on
    //   ball one — the load-bearing law), rolling with the heat after that.
    //   It pays the old cart ticket. Zero new walls, so zero new traps.
    c.push(SensorCircle(WILLIE_C.x + TRAIN_R * Math.cos(TRAIN_PARK), WILLIE_C.y + TRAIN_R * Math.sin(TRAIN_PARK), TRAIN_HIT_R,
      { tag: "train", id: "train" }));

    // ── 🕳 THE HATCH — a hole, which is an ABSENCE ──────────
    // There is no wall here. No floor, no geometry at all — just a
    // sensor and a hook. That is the only way to model a hole in a
    // solid-body solver, and it is also exactly how a real
    // under-playfield tunnel works: the ball vanishes at one hole
    // and reappears at another, and no player in seventy years has
    // ever felt cheated by it.
    //
    // Falls through and spits you out high on the Big Top, at speed.
    // A shortcut that costs you the ride.
    // ⚠ MOVED OUT OF THE CANNON'S SHADOW. The hatch used to sit at
    //   (328,522), directly BELOW the funnel that now guards the
    //   bottom-right — and the funnel's mouth faces up, so every
    //   ball rolling down that flank was swallowed before it could
    //   ever reach the hole. Forty simulated games: hatch fired
    //   ZERO times. That is the 0/120 dead-content bug in a new
    //   costume — a fifteenth ticket with no path to it, which
    //   makes ROAR unreachable even WITH the Ghost Train key. The
    //   hatch lives in the mid-band now, on the cart's spill line,
    //   where traffic actually falls.
    // ★ W3: THE HATCH IS WILLIE'S TUNNEL. Its mouth is the front of his
    //   stage; fall in and you shoot out of his back, straight up at
    //   the Ghost Train's door. At MELTDOWN, the trip through him IS
    //   the heart strike (see onSensor "hole").
    c.push(...dropHole({ x: WILLIE_C.x, y: TUNNEL_IN_Y, r: 12, id: "hatch", tag: "hole" }));

    // ── 🎯 THE CAPTIVE — the one you punch ──────────────────
    // A ball in a socket. You cannot collect it. You HIT it, and it
    // rattles, and the rattle scores. The oldest trick on a real
    // machine and the cheapest thrill on this one.
    c.push(...captive({ x: 36, y: 200, r: 9, id: "cap_1", tag: "captive" }));

    // ── STRUCTURAL CHECKS ────────────────────────────────────
    // Three laws, three different blindnesses. A crash on boot is
    // infinitely better than a ball that silently goes nowhere.
    //
    // ⚠ THE RIDES GET NO EXEMPTION. A loop is not above the wedge
    //   law; a scoop is not above the ledge law. The ONE thing a
    //   catching device is allowed is to CATCH — and only because it
    //   ships with the ejector that lets go.

    // Mount the Pit's magnet alongside the Bellows. Same array, same
    // engine read, one source of truth.
    FOUNDRY.fields = [
      GravityZone(20, 240, 340, 200, { id: "bellows", mul: 1 }),
      _pool.field,
    ];

    return assertTableSane(c, "The Foundry", 7);
  },

  // ══════════════════════════════════════════════════════════════
  //  THE BELLOWS — permanent gravity zone, gated by phase
  // ══════════════════════════════════════════════════════════════
  //
  // `fields` are declared once and mounted permanently. I cannot add
  // or remove them per-phase without reaching into the engine, and I
  // will not reach into the engine — a table that needs an engine
  // change to work is a table that has failed.
  //
  // So the zone is ALWAYS mounted and its `gravityMul` is what
  // breathes. At DORMANT it is pinned at exactly 1, and a gravity
  // zone multiplying gravity by one is, by definition, not there.
  //
  //     THE BELLOWS DO NOT MAKE YOUR SHOTS FAIL.
  //     THEY MAKE YOUR SHOTS *EARLY* OR *LATE*.
  //
  // That distinction is the whole difference between a mechanic and
  // an insult. A random force is noise, and a player learns nothing
  // from dying to it. A PERIODIC one is a puzzle: the shot you know
  // still works, and your job is to find out WHEN.
  // ⚠ THE PIT'S MAGNET IS MOUNTED IN build(), NOT HERE.
  //   `whirlpool()` returns { colliders, field } — and a device that
  //   returns a field you drop on the floor is a bowl with no pull:
  //   it looks IDENTICAL in a screenshot and does nothing at all.
  //   build() pushes it onto this array (see the tail of build), so
  //   the geometry and its field are guaranteed to agree because
  //   they are created in the same breath.
  fields: [
    GravityZone(20, 240, 340, 200, { id: "bellows", mul: 1 }),
  ],

  // ── Scoring ────────────────────────────────────────────────
  scoreTable: {
    bumper:    110,
    slingshot:  70,
    target:    260,
    ramp:     1400,
    orbit:    1100,
    spinner:    40,
    crucible:    0,       // gated — see onSensor
    heart:       0,       // gated — see onSensor
    vault:       0,       // gated — see onSensor
    piston:     30,       // a dark piston is worth a pittance...
    post:        5,

    // ★★★ THE RIDES
    loop:      2200,      // a COMPLETED orbit. the best non-secret shot.
    turnstile:   90,
    captive:    260,
    train:        0,      // W4: gated — the train pays in onSensor (600), where the tunnel-exit grace applies
    scoop:        0,      // gated — the scoop pays via the SIDESHOW
    hole:         0,      // a hole is a ride, not a score
    pool:         0,      // gated — the pit pays on the re-serve
  },
  chargeTable: {
    bumper:    3,
    slingshot: 2,
    target:    6,
    ramp:      13,
    orbit:     11,
    spinner:   1,
    piston:    1,
    crucible:  0,
    heart:     0,
    vault:     0,
    drain:    -15,

    // ★★★ THE RIDES
    loop:      14,
    turnstile:  2,
    captive:    5,
    scoop:     12,
    train:      0,        // W4: gated — see onSensor
  },

  startCharge: 30,

  // ══════════════════════════════════════════════════════════════
  //  ★★★ THE RINGMASTER — the being under the big top
  // ══════════════════════════════════════════════════════════════
  //
  // The Forgemaster's machinery — heat, bellows, hammers, the
  // meltdown window — is UNTOUCHED. All of that is the boss's
  // OFFENSE, and it already worked. What was missing was a body:
  // the old heart was a one-touch kill switch, which made the
  // climax a single lucky sensor trip. Now the heart is a WOUND
  // in a BEING, and the being has the two numbers every being in
  // the Empire has:
  //
  //   ANIMA 24 — its life. Persists across YOUR drains (the Soul
  //              side of the line). Dim it to zero and the
  //              Ringmaster is FELLED — the biggest single event
  //              on the table, and in a duel, the win itself.
  //   LYF    6 — its guard. RESTORES IN FULL on every drain (the
  //              Lyf Cycle, R27/28). Chip damage is worthless:
  //              you crack the guard THIS BALL — with combo-hot
  //              strikes, because Lyt is combo heat and you
  //              cannot break a wall by cradling — or the wall
  //              heals and you start over.
  //
  // Guard down = FRACTURED for the rest of the ball. While it is
  // open, heart strikes dim Anima by your Lyt, and EVERY Impera
  // you cast lands on it for level × 2 — the ball opens the
  // door; the Impera walk through it. Felling takes roughly two
  // good meltdowns. It is supposed to. A boss you fell by
  // accident is a piñata.
  //
  // ⚠ THE HEART IS STILL GATED AT MELTDOWN in onSensor. The boss
  //   block does not repeal the phase ladder — it deepens what
  //   the open window is FOR.
  //
  // The MOVES are BossKit's universal effects wearing the
  // Cirque's costume (the reskin law: the kit owns what a move
  // DOES, the table owns what it is CALLED). menace() maps the
  // AI's aggression straight onto the heat ladder, so the oldest
  // law on this table — DORMANT MEANS DORMANT, the table may
  // never attack someone who hasn't earned it — holds for the
  // Ringmaster the same way it holds for the hammers.
  boss: {
    name:  "THE RINGMASTER",
    title: "Keeper of the Cirque",
    anima: 24,
    lyf:   6,
    moves: [
      { id: "fracture", name: "THE HUSH" },        // your flippers, silenced
      { id: "hasten",   name: "THE STAMPEDE" },    // your ball, spurred
      { id: "lure",     name: "THE BARKER'S CALL" }, // the drain, singing
      { id: "drag",     name: "THE TENT COMES DOWN" }, // gravity, heavier
      { id: "jolt",     name: "A COLD HAND" },     // the cheap shove
    ],
    // The three a HUMAN opponent may drive in a duel (plus the
    // guardian verbs, which every duel rail carries).
    duelKit: ["fracture", "hasten", "lure"],
    menace: (engine) => FOUNDRY._s().phase,
  },

  // ══════════════════════════════════════════════════════════════
  //  MODE STATE
  // ══════════════════════════════════════════════════════════════
  //
  // ⚠ THE SINGLETON TRAP — READ THIS BEFORE ADDING A COUNTER.
  //
  // FOUNDRY is a MODULE-LEVEL OBJECT, imported once and SHARED BY
  // EVERY ENGINE THAT EVER RUNS in the session. Its `_state` lives
  // on that shared object.
  //
  // The engine nulls `table._state` on construction — a new engine
  // is a new game — but that safety net only works because every
  // counter that ESCALATES lives in here, and nowhere else.
  //
  // The last time someone forgot, Spite's jackpot counter never
  // reset, and by the 200th game of a session the table paid 1.2
  // MILLION points per shot. The verification run's MEDIAN score was
  // fifty million. Not an outlier — the median. Every number in every
  // scoreTable was silently meaningless.
  //
  // `kills`, `jackpots`, `vaults` and `tickets` below are all exactly
  // that kind of state. If you ever find yourself hanging an
  // escalating value on the module object, or on a closure, or
  // anywhere that outlives one engine — you have just rebuilt that
  // bug.
  _state: null,
  _init() {
    this._state = {
      // ── the table's mode ──
      lit: new Set(),          // targets down
      crucibleLit: false,
      crucibleTimer: 0,
      jackpots: 0,

      // ── ★ the midway ──
      tickets: new Set(),      // which attractions have been worked
      gateOpen: false,         // the vault is live
      roar: false,             // bumpers boosted
      vaults: 0,               // ★ ESCALATING

      // ── ★★★ the rides ──
      loops: 0,                // ★ ESCALATING — see the singleton trap
      sideshow: 0,             // seconds left on the SIDESHOW mode
      spins: 0,                // wheel spins taken
      trainA: TRAIN_PARK,      // W4: the circus train's angle round Willie

      // ── the Forgemaster ──
      heat: 0,
      phase: 0,                // 0 dormant / 1 stoking / 2 awake / 3 meltdown
      beat: 0,
      step: 0,
      hot: null,               // the id of the piston currently LIVE
      hotFor: 0,
      bellows: 0,
      meltTimer: 0,
      kills: 0,                // ★ ESCALATING
      strikes: 0,
    };
  },
  _s() {
    if (!this._state) this._init();
    return this._state;
  },

  /** Stoke the furnace. The ONLY way heat ever goes up. */
  _stoke(engine, n) {
    const s = this._s();
    if (s.phase >= 3) return;                 // already melting.
    const was = s.phase;
    s.heat = Math.min(HEAT_MAX, s.heat + n);

    s.phase = s.heat >= PHASE_MELT  ? 3
            : s.heat >= PHASE_AWAKE ? 2
            : s.heat >= PHASE_STOKE ? 1
            : 0;

    if (s.phase === was) return;

    // ── A PHASE CHANGE IS THE LOUDEST EVENT ON THIS TABLE ────
    // The player MUST know the rules just changed, at the instant
    // they change, or the first thing the new phase does will read
    // as a bug. An unannounced difficulty spike is indistinguishable
    // from a broken table — and the player will blame THEMSELVES,
    // which is worse than blaming us.
    if (s.phase === 1) {
      engine._push("mode", "▓ THE BELLOWS BREATHE");
      engine._fx({ fx: "mode", label: "STOKING" });
    }
    if (s.phase === 2) {
      engine._push("mode", "▓▓ THE RINGMASTER WAKES — mind the hammers");
      engine._fx({ fx: "mode", label: "AWAKE" });
      engine.multiplier = Math.max(engine.multiplier, 2);
    }
    if (s.phase === 3) {
      s.meltTimer = 22;
      engine._push("mode", "▓▓▓ MELTDOWN — STRIKE THE HEART");
      engine._fx({ fx: "jackpot", label: "MELTDOWN", at: { x: WILLIE_C.x, y: WILLIE_C.y } });
      engine.multiplier = 3;
    }
  },

  // ══════════════════════════════════════════════════════════════
  //  ★ TICKETS — the unlock ladder
  // ══════════════════════════════════════════════════════════════
  //
  // A ticket is awarded the FIRST time an attraction is worked, and
  // never again. That is the entire anti-farm mechanism, and it is
  // one line: `if (s.tickets.has(which)) return;`
  //
  // It is a Set, not a counter, precisely BECAUSE a counter is the
  // shape of the bug that has already cost this codebase two
  // catastrophes (the sensor farm; the Spite jackpot). A Set of
  // distinct ids cannot inflate. It can only fill up.
  _ticket(engine, which, label) {
    const s = this._s();
    if (s.tickets.has(which)) return;
    s.tickets.add(which);
    const n = s.tickets.size;

    engine.score += 500 * n;
    engine._push("target", `🎟 TICKET ${n}/${TICK_TOTAL} — ${label}`);
    engine._fx({ fx: "mode", label: `TICKET ${n}` });

    if (n === TICK_WHEEL) {
      engine.multiplier = Math.max(engine.multiplier, 2);
      engine._push("mode", "★ THE WHEEL SPINS — ×2");
    }
    if (n === TICK_GATE) {
      s.gateOpen = true;
      engine._push("mode", "★ THE GATE OPENS — the Ghost Train is live");
      engine._fx({ fx: "mode", label: "GATE OPEN" });
    }
    if (n === TICK_ROAR) {
      s.roar = true;
      // Mutate `kick` on the live colliders. One field, on objects
      // the engine already owns, read fresh by the solver every
      // step. Nothing added, nothing removed, nothing to desync —
      // the same mechanism the hammers use, for the same reason.
      for (const id of BUMPERS) {
        const b = engine.world.getCollider(id);
        if (b) b.kick = Math.round(BMP_KICK * 1.4);
      }
      engine._push("mode", "★★ THE MIDWAY ROARS");
      engine._fx({ fx: "jackpot", label: "ROAR", at: { x: WILLIE_C.x, y: WILLIE_C.y - RING_R } });
    }
  },

  // QUICK CO-OP (tiltRelay.js): a restored table re-arms what THE MIDWAY ROARS changed on the live
  // colliders (the bumpers' kick), because a fresh engine builds them at their base kick.
  relayRoar(engine) {
    for (const id of BUMPERS) {
      const b = engine.world.getCollider(id);
      if (b) b.kick = Math.round(BMP_KICK * 1.4);
    }
  },

  onHit(engine, e) {
    const s = this._s();

    // ── TICKETS: the Big Top and the Carousel ────────────────
    if (e.tag === "bumper") this._ticket(engine, "bigtop",   "THE BIG TOP");
    if (e.tag === "post")   this._ticket(engine, "carousel", "THE CAROUSEL");

    // ── ★★★ TICKETS: the rides ───────────────────────────────
    if (e.tag === "turnstile") this._ticket(engine, "turnstile", "THE TURNSTILE");
    // (W4: the cart ticket is paid by the circus train — see onSensor)

    // The captive ball. Punch it HARD and it pays. A soft nudge is
    // not a hit — the speed gate is what makes it a SHOT and not a
    // thing you brush past on your way somewhere else.
    if (e.tag === "captive" && e.speed > 220) {
      this._ticket(engine, "captive", "THE HIGH STRIKER BELL");
    }

    // ── TARGETS: score, light the crucible, AND stoke the fire ──
    if (e.tag === "target") {
      if (!s.lit.has(e.id)) {
        s.lit.add(e.id);
        engine.score += 400;
        engine._push("target", `Target ${s.lit.size}/4`);
        this._stoke(engine, 1);
        if (s.lit.size >= 4) {
          s.crucibleLit = true;
          s.crucibleTimer = 12;
          this._ticket(engine, "gallery", "THE SHOOTING GALLERY");
          engine._push("mode", "★ CRUCIBLE LIT — hit the right ramp");
          engine._fx({ fx: "mode", label: "CRUCIBLE" });
        }
      }
    }

    // ── PISTON CONTACT ────────────────────────────────────────
    // A DARK piston is just a wall — the engine already scored it
    // and there is nothing more to say. A HOT one just SLAMMED you,
    // and the player needs to be told WHY their ball suddenly went
    // somewhere they did not send it. An unexplained impulse is the
    // most infuriating thing a pinball table can do to a person.
    // The hammer is allowed to hit you. It is not allowed to do it
    // QUIETLY.
    if (e.tag === "piston" && s.hot === e.id) {
      s.strikes += 1;
      engine._push("hit", "◈ HAMMER");
      engine._fx({ fx: "pop", at: { ...e.point }, pts: 0, tag: "piston" });
    }
  },

  onSensor(engine, e) {
    if (e.tag === "train" && !(e.ball?.trainGrace > engine.elapsed)) {   // W4: caught the circus train
      this._ticket(engine, "cart", "THE CIRCUS TRAIN");
      engine.score += 600 * engine.multiplier;
      engine.charge = Math.min(engine.charge + 6, 100);
    }
    const s = this._s();

    // ── TICKETS: the ramps, the orbit, the spinner ───────────
    if (e.tag === "ramp")    this._ticket(engine, "ramp",    "THE HELTER-SKELTER");
    if (e.tag === "orbit")   this._ticket(engine, "orbit",   "THE GRAND ORBIT");
    if (e.tag === "spinner") this._ticket(engine, "spinner", "THE SPINNER");

    // ══════════════════════════════════════════════════════════
    // ★★★  THE RIDES
    // ══════════════════════════════════════════════════════════
    //
    // SIDESHOW pays TRIPLE. The mode is lit by the scoop, and it is
    // the one window where the midway is worth more than the boss.
    const boost = s.sideshow > 0 ? 3 : 1;

    // ── ⭕ THE LOOP ──────────────────────────────────────────
    //
    // ⚠ THE SENSOR IS AT THE FAR SIDE OF THE ORBIT, NOT AT THE
    //   MOUTH, AND THAT IS THE WHOLE DESIGN. A sensor at the mouth
    //   answers "did the ball touch the loop?" — which a ball that
    //   brushed the entrance and fell straight back out also answers
    //   YES to. The question a loop asks is "DID THE BALL GO ROUND",
    //   and only geometry on the far side can ask it.
    //
    //   Same law the orbit already lives by: score on EXIT, not
    //   entry. You have to COMPLETE it, not clip it.
    if (e.tag === "loop") {
      this._ticket(engine, "loop", "THE LOOP-THE-LOOP");
      s.loops += 1;
      const pts = 2200 * boost * Math.min(s.loops, 5);
      engine.score += pts;
      engine._push("jackpot", `⭕ LOOP ×${s.loops} — ${pts.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { ...e.point }, pts });
      this._stoke(engine, 1);          // riding the midway stokes the fire
    }

    // ── 🎪 THE SIDESHOW — lit by the scoop ──────────────────
    // The ball SAT DOWN (dwell). So: light the mode, hold the beat
    // so the player can SEE it land, then THROW the ball back out —
    // hard, up and right, into the Big Top, so the window opens with
    // the table already screaming.
    if (e.tag === "scoop") {
      this._ticket(engine, "sideshow", "THE SIDESHOW");
      s.sideshow = 9.0;
      engine.charge = Math.min(engine.charge + 12, 100);
      engine._push("mode", "🎪 SIDESHOW — the midway pays TRIPLE");
      engine._fx({ fx: "mode", label: "SIDESHOW" });

      // 0.45s: long enough to register, short enough that it never
      // reads as the game having hung. A pause the player does not
      // understand is indistinguishable from a freeze.
      const b = e.ball;
      // ⚠ THE EJECT IS A WARP, NOT A VELOCITY WRITE, AND THE CUP'S
      //   OWN GEOMETRY IS WHY. The mouth spans face ± 0.62 rad, but
      //   a 7u ball leaving from the bottom of a 16u cup needs
      //   ~0.46 rad of that just for its own radius — the real exit
      //   cone is ±9° around straight up. ejectFrom() at any lateral
      //   angle CLIPS THE LIP (restitution 0.05, dead — straight
      //   back into the cup, sensor latch still held: entombed), and
      //   straight up simply falls straight back in. Measured both.
      //   A straight line cannot leave this cup and also leave this
      //   AREA. So the scoop does what the hatch, the chute and the
      //   cannon already do: it WARPS — lifted to the lip, thrown
      //   up-right, into the Big Top. Same verb, same honesty.
      // W3: (118,372) sat 0.9u from the loop wall — the ball spawned
      //   INSIDE it (pre-existing, found by review). Now it pops out
      //   clear of the loop and flies up-right into Willie's ring.
      engine.addTimer(0.45, () => warpBall(b, 132, 352, 200, -360));
    }

    // ── 🎪 THE HUMAN CANNONBALL — swallowed, then FIRED ─────
    // The funnel eats you bottom-right; the muzzle is at the top.
    // ⚠ THE MUZZLE IS NOT AT THE FUNNEL. That is the entire fix for
    //   D17 (see the whirlpool in build): warpBall clears the
    //   ball's sensor latch, so a re-serve INSIDE the eye re-fires
    //   the eye every step, forever. The exit must be outside the
    //   eye AND outside the magnet — this one is 300+ units away.
    if (e.tag === "pool") {
      this._ticket(engine, "pit", "THE CANNON");
      const rng = engine.world.rng;             // LAW 4. never Math.random.
      const pts = 1200 * boost;
      engine.score += pts;
      engine.charge = Math.min(engine.charge + 8, 100);
      // Fired from the muzzle, top-right, sailing LEFT over the
      // tents. Speed jitters (seeded), so the landing spot varies —
      // the drunk re-serve survives, it just happens in the air.
      // W3: the muzzle rose to (262,62), over the Ghost Train's roof. At the
      //   old (302,128) every shot hit the moved vault's right rib and 41%
      //   fell straight back into the pool — a self-feeding farm (review).
      //   Now: 2/120 come back, and it lands left, as designed.
      warpBall(e.ball, CANNON_MUZZLE.x, CANNON_MUZZLE.y, -(200 + rng() * 100), -40 + rng() * 40);
      engine._push("jackpot", `🎪 THE HUMAN CANNONBALL — ${pts.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { ...CANNON_MUZZLE }, pts });
    }

    // ── 🕳 THE HATCH — falls through, comes out on the Big Top ──
    if (e.tag === "hole") {
      this._ticket(engine, "hatch", "THE HATCH");
      const rng = engine.world.rng;           // LAW 4
      const shot = e.ball.vel.y < 0;          // rising = you SHOT it in; falling = it dropped back
      // W3: out of his back, thrown up-LEFT. Straight up hit the Ghost
      //   Train door and fell back into the mouth 23% of the time (review);
      //   up-left lands on the carousel/arch, 5% come back.
      warpBall(e.ball, WILLIE_C.x, TUNNEL_OUT_Y, -240 + rng() * 50, -400 + rng() * 40);
      e.ball.trainGrace = engine.elapsed + 0.5;   // the exit sits on the train's lap: no free catch on the way out
      engine._push("mode", "🕳 THROUGH WILLIE");
      // At MELTDOWN, a trip through him is the heart strike — but only a
      //   ball you SHOT in. One that falls back on its own is just a ride.
      if (s.phase >= 3 && shot) this.onSensor(engine, { ...e, tag: "heart", point: { x: WILLIE_C.x, y: WILLIE_C.y } });
      engine._fx({ fx: "vent" });
    }

    // ── 🎡 THE BIG WHEEL — six slots, six prizes ────────────
    //
    // ⚠ THE PAYOUTS ARE LOPSIDED ON PURPOSE, AND ONE SLOT PAYS
    //   NOTHING. A wheel where every wedge is worth the same is not
    //   a wheel — it is a rollover with extra steps. The entire
    //   feeling of a prize wheel is that you can SEE the good slot
    //   and you cannot HIT it, and a blank is what makes the jackpot
    //   slot mean anything at all.
    if (e.tag && e.tag.startsWith("prize_")) {
      this._ticket(engine, "wheel", "THE BIG WHEEL");
      s.spins += 1;
      const slot = +e.tag.split("_")[1];
      const prizes = [
        { pts:  600, chg:  0, msg: "🎟 A ticket. Congratulations." },
        { pts: 3000, chg:  0, msg: "🎯 3,000" },
        { pts:    0, chg: 30, msg: "⚡ +30 CHARGE" },
        { pts: 9000, chg:  0, msg: "💰 JACKPOT — 9,000" },
        { pts:    0, chg:  0, msg: "💀 NOTHING. Spin again." },
        { pts: 1500, chg: 12, msg: "🎪 1,500 + charge" },
      ];
      const pz = prizes[slot % prizes.length];
      if (pz.pts) engine.score += pz.pts * boost;
      if (pz.chg) engine.charge = Math.min(engine.charge + pz.chg, 100);
      engine._push("mode", pz.msg);
      if (pz.pts >= 3000) {
        engine._fx({ fx: "jackpot", at: { ...e.point }, pts: pz.pts * boost });
      }
    }

    // ── THE CRUCIBLE — the jackpot ───────────────────────────
    if (e.tag === "crucible") {
      if (!s.crucibleLit) return;             // inert. shot wasted. correct.
      s.crucibleLit = false;
      s.crucibleTimer = 0;
      s.lit.clear();
      s.jackpots += 1;

      const jp = 8000 * s.jackpots;
      engine.score += jp;
      engine.charge = Math.min(engine.charge + 30, 100);
      engine._push("jackpot", `JACKPOT ×${s.jackpots} — ${jp.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { ...e.point }, pts: jp });

      // The crucible is a FORGE. Firing it stokes the furnace hard —
      // the fast lane to the boss, and it is exactly the shot a good
      // player is already making. You do not have to learn a new
      // skill to wake the Forgemaster. You have to do the thing you
      // were already doing, WELL.
      this._stoke(engine, 3);
    }

    // ── ★ THE VAULT — the Ghost Train's prize ────────────────
    //
    // The gate is the FIFTH ticket. Reach this room before then and
    // the table shrugs: no points, no charge, no hint. That is the
    // same law the crucible taught — a shot that pays out when it
    // isn't lit is not a shot, it's a slot machine — but here it is
    // doing a second job as well.
    //
    // You had to work five attractions to open the gate. So by the
    // time you can even collect this, you have already LEARNED THE
    // TABLE. The secret cannot be stumbled into on ball one, and a
    // player who finds it has earned every part of it.
    if (e.tag === "vault") {
      if (!s.gateOpen) {
        engine._push("cool", "The gate is shut. Work the midway.");
        return;
      }
      s.vaults += 1;

      // ★ THE SEVENTH TICKET. The Ghost Train is an attraction like
      //   any other -- it is simply the only one you need a POWER to
      //   work. Collecting it is what tips the midway into ROAR.
      this._ticket(engine, "ghosttrain", "THE GHOST TRAIN");

      // ★ ESCALATING PAYOUT. Lives on _state, which the engine nulls
      //   per game. See the singleton warning above — this is the
      //   exact shape of the counter that once paid fifty million
      //   points and made every number in the game a lie.
      const pts = 40000 * s.vaults;
      engine.score += pts;
      engine.charge = 100;                    // a full meter. you earned it.
      engine._push("jackpot", `☠ THE GHOST TRAIN ×${s.vaults} — ${pts.toLocaleString()}`);
      engine._fx({ fx: "jackpot", at: { ...e.point }, pts });
      this._stoke(engine, 4);                 // and it makes the boss angry.
    }

    // ── THE HEART — the wound ────────────────────────────────
    //
    // ⚠ THIS USED TO BE A ONE-TOUCH KILL, and the fell was a
    //   single lucky sensor trip. Now the heart is where you
    //   STRIKE THE RINGMASTER: the engine's bossStrike() runs the
    //   Lyf Cycle (guard first, then the Fracture window, then
    //   Anima). Still gated at MELTDOWN — hit it before then and
    //   nothing happens, no points, no hint. A shot that pays out
    //   when it isn't lit is a slot machine.
    //
    //   Each clean strike on the open heart pays a fixed 4,000 —
    //   deliberately smaller than the old 25k kill, because the
    //   kill is a CAMPAIGN now and the campaign's payout lives in
    //   onBossFelled below.
    if (e.tag === "heart") {
      if (s.phase < 3) return;                // INERT. no points, no hint.
      const r = engine.bossStrike(e);
      if (!r) return;                         // felled already, or dead phase
      s.strikes += 1;
      const pts = 4000;
      engine.score += pts;
      engine.charge = Math.min(engine.charge + 10, 100);
      engine._fx({ fx: "pop", at: { ...e.point }, pts, tag: "heart" });
    }
  },

  // ══════════════════════════════════════════════════════════════
  //  ★ THE FELL — the engine dims the Ringmaster to zero and
  //  calls back here for the consequences, because the PAYOUT and
  //  the FURNACE are the table's business, not the engine's.
  // ══════════════════════════════════════════════════════════════
  onBossFelled(engine) {
    const s = this._s();
    s.kills += 1;                             // ★ ESCALATING — singleton law

    const pts = 60000 * s.kills;
    engine.score += pts;
    engine.charge = 100;                      // a full meter. you earned it.

    // The furnace is EXTINGUISHED. All the way to zero, and the
    // whole climb starts again.
    //
    // ⚠ heat resets to 0, NOT to PHASE_AWAKE. I tried leaving it
    //   hot ("keep the pressure on!") and it is a trap: the boss
    //   would come back in seconds, forever, and the player would
    //   never get a single quiet moment to just PLAY PINBALL. A
    //   boss you can never put down is not an escalation. It's an
    //   environment, and you stop hearing it.
    //
    //   THE SILENCE AFTER THE KILL IS PART OF THE KILL.
    //
    // And the being RISES for the next act — Anima and guard
    // restored, one rung meaner (its next fell pays more, and
    // the AI's appetite rides the heat ladder you'll have to
    // climb again to reach it). Same encore shape the old kills
    // counter always had; the counter just grew a body.
    s.heat = 0;
    s.phase = 0;
    s.meltTimer = 0;
    s.hotFor = 0;
    engine.multiplier = s.tickets.size >= TICK_WHEEL ? 2 : 1;
    // ⚠ NOTE WHAT IS *NOT* HERE: `s.hot = null`.
    //   It was, and it was the bug. See the cleanup branch in
    //   onTick — nulling the pointer HERE destroyed the only record
    //   of which hammer was still armed, and left it armed forever.
    //   The cleanup branch nulls it, AFTER it has cooled the metal.

    engine._push("jackpot", `☠ THE RINGMASTER FALLS ×${s.kills} — ${pts.toLocaleString()}`);
    engine._fx({ fx: "jackpot", at: { x: WILLIE_C.x, y: WILLIE_C.y }, pts });

    if (engine.boss) {
      const B = engine.boss;
      B.felled = false;                       // the show must go on (solo encore)
      B.anima = B.animaMax;
      B.lyf = B.lyfMax;
      B.fractured = B.lyfMax === 0;
      B.khao = 0;
    }
  },

  onTick(engine, dt) {
    const s = this._s();

    // ══════════════════════════════════════════════════════════
    // ★★★  THE MOVING RIDES
    // ══════════════════════════════════════════════════════════
    //
    // This is the ONLY place a table may mutate its own geometry,
    // and it is driven by the engine's fixed-step clock — so the
    // turnstile is in the same place on a 144Hz panel as on a 60Hz
    // one. The carnival is chaotic. It is not random, and it is not
    // frame-dependent. Determinism survives the midway.
    if (_turn) _turn.spin(dt);
    // W4: the circus train laps Willie, speed by heat (parked while dormant)
    {
      const a0 = s.trainA ?? TRAIN_PARK;
      if (s.phase > 0) s.trainA = a0 + TRAIN_SPEED[s.phase] * dt;
      else {
        // back to dormant (Willie felled, or the heat cooled): the train rolls ON to
        // its park spot and stops there, instead of freezing wherever it was
        // (possibly right over the tunnel mouth). Review fix.
        const left = ((TRAIN_PARK - a0) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
        s.trainA = left < 1e-3 || left > 2 * Math.PI - 1e-3 ? a0 : a0 + Math.min(left, TRAIN_SPEED[1] * dt);
      }
      const tr = engine.world.getCollider("train");
      if (tr) tr.c = { x: WILLIE_C.x + TRAIN_R * Math.cos(s.trainA), y: WILLIE_C.y + TRAIN_R * Math.sin(s.trainA) };
    }

    // ── THE SIDESHOW burns down ──────────────────────────────
    if (s.sideshow > 0) {
      s.sideshow = Math.max(0, s.sideshow - dt);
      if (s.sideshow === 0) {
        engine._push("cool", "Sideshow over. Back to the fire.");
      }
    }

    // ── The crucible cools ───────────────────────────────────
    if (s.crucibleTimer > 0) {
      s.crucibleTimer -= dt;
      if (s.crucibleTimer <= 0) {
        s.crucibleLit = false;
        s.lit.clear();
        engine._push("cool", "Crucible cooled");
      }
    }

    // ══════════════════════════════════════════════════════════
    //  THE BELLOWS  (phase ≥ 1)
    // ══════════════════════════════════════════════════════════
    // One number, on an object the engine already owns and already
    // ticks, read fresh by the solver every step. No field added,
    // none removed, nothing to desync — and NO ENGINE CHANGE.
    const bel = engine.world.fields.find((f) => f.id === "bellows");
    if (bel) {
      if (s.phase === 0) {
        bel.gravityMul = 1;                   // the definition of "not there"
      } else {
        // A SINE — periodic, learnable, countable. Not noise. Never
        // noise. Amplitude 0.35 at STOKING (a drift) up to 0.6 at
        // MELTDOWN (a genuine heave).
        const amp  = 0.35 + 0.125 * (s.phase - 1);
        const rate = 1.5  + 0.55  * (s.phase - 1);
        s.bellows += dt * rate;
        bel.gravityMul = 1 + Math.sin(s.bellows) * amp;
      }
    }

    // ══════════════════════════════════════════════════════════
    //  THE PISTONS  (phase ≥ 2)
    // ══════════════════════════════════════════════════════════
    //
    // The hammers fire in strict sequence: A, D, C, A, D, C. It is a
    // LOOP, not a shuffle, and that is the entire reason the phase is
    // playable.
    //
    // ⚠ I WROTE THIS RANDOM FIRST. It was awful, and awful in a way
    //   that took a minute to name: a random hammer is not HARDER
    //   than a sequenced one, it is just UNREADABLE. The player
    //   cannot plan, so they cannot improve, so every death is the
    //   table's fault instead of theirs — and a player who never
    //   feels at fault never feels like they are getting better.
    //
    //   A BOSS MUST BE BEATABLE ON THE SECOND ATTEMPT BY SOMEONE WHO
    //   PAID ATTENTION ON THE FIRST. That is not a nicety. It is the
    //   definition of a boss as opposed to a hazard.
    if (s.phase >= 2) {
      if (s.hotFor > 0) {
        s.hotFor -= dt;
        if (s.hotFor <= 0 && s.hot) {
          const c = engine.world.getCollider(s.hot);
          if (c) c.kick = 0;                  // back to a dull, honest wall
          s.hot = null;
        }
      }

      s.beat += dt;
      // MELTDOWN drives the hammers ~30% faster. Not double — double
      // crosses the readability floor. The climax should feel like
      // the walls are closing in, not like the frame rate broke.
      const cycle = s.phase >= 3 ? PISTON_CYCLE * 0.7 : PISTON_CYCLE;

      if (s.beat >= cycle) {
        s.beat -= cycle;
        const id = HAMMERS[s.step % HAMMERS.length];
        s.step += 1;

        const c = engine.world.getCollider(id);
        if (c) {
          // ⚠ 300 IS A CEILING, NOT A FLAVOUR. The solver clamps at
          //   MAX_SPEED=900 and sub-steps any fast ball, so a huge
          //   kick won't TUNNEL — the engine is safe from me. But it
          //   WILL rocket the ball across the table faster than a
          //   human can track, and A BALL YOU CANNOT SEE IS A BALL
          //   YOU CANNOT PLAY. The hammer must hit hard enough to
          //   ruin your shot and not so hard that it ruins your eyes.
          c.kick = s.phase >= 3 ? 340 : 300;
          s.hot = id;
          s.hotFor = PISTON_LIVE;
        }
      }
    } else {
      // ══════════════════════════════════════════════════════════
      //  ★ COOLING THE HAMMERS — and the bug that lived here
      // ══════════════════════════════════════════════════════════
      //
      // ⚠ MY FIRST VERSION READ:
      //
      //       if (s.hot) { getCollider(s.hot).kick = 0; s.hot = null; }
      //
      //   It is obviously correct and it FAILED 400 TIMES OUT OF 400.
      //
      //   Because `onSensor` — the kill — had already set `s.hot =
      //   null` on its way out. So by the time this branch ran, the
      //   pointer to the still-armed hammer WAS ALREADY GONE. The
      //   guard read null, did nothing, and the collider kept its
      //   `kick: 300` FOREVER: a live, ball-launching hammer in the
      //   middle of a table the game had just told the player was
      //   DORMANT.
      //
      //   Two functions, each individually correct, that together
      //   lose the one fact the cleanup depended on.
      //
      // ── THE FIX IS TO STOP ASKING ────────────────────────────
      //   Don't ask WHICH hammer is armed. You are asking a state
      //   variable to remember something across a function boundary,
      //   and that is precisely the thing that just failed. Cool ALL
      //   THREE, unconditionally, every frame the boss is down. It
      //   costs three assignments and it CANNOT be wrong, because it
      //   does not depend on remembering anything.
      //
      //       AN INVARIANT THAT DEPENDS ON A POINTER BEING INTACT
      //       IS NOT AN INVARIANT. IT IS A HOPE.
      for (const id of HAMMERS) {
        const c = engine.world.getCollider(id);
        if (c && c.kick !== 0) c.kick = 0;
      }
      s.hot = null;
      s.hotFor = 0;
      s.beat = 0;
      s.step = 0;
    }

    // ══════════════════════════════════════════════════════════
    //  MELTDOWN — the window closes
    // ══════════════════════════════════════════════════════════
    // It cools to AWAKE and not to DORMANT, because a total reset
    // would punish the player for the crime of ALMOST winning, and
    // the miss is punishment enough. You keep the fight. You just
    // lose the shot.
    if (s.phase === 3) {
      s.meltTimer -= dt;
      if (s.meltTimer <= 0) {
        s.phase = 2;
        s.heat  = PHASE_AWAKE;
        engine.multiplier = 2;
        engine._push("cool", "The heart closes. Stoke it again.");
      }
    }
  },

  // ══════════════════════════════════════════════════════════════
  //  LAMPS — and why this table needs NO renderer changes
  // ══════════════════════════════════════════════════════════════
  //
  // `lamps.targets` is not really "targets." It is the renderer's
  // generic LIT-COLLIDER CHANNEL: Playfield.js takes every id in it
  // and re-draws that collider, bright, every frame, on top of the
  // static bake.
  //
  // So the currently-HOT hammer goes in it. The hammer that is about
  // to hit you is the hammer that is GLOWING, because THE VERY SAME
  // STATE DRIVES BOTH and they cannot possibly get out of sync —
  // there is no second source of truth to drift from.
  //
  //     THE TELL AND THE THREAT ARE THE SAME OBJECT.
  //
  // ⚠ ONLY `targets`, `nodes`, `grudge` and `crucible` EXIST as lamp
  //   channels. Anything else I put here is DEAD DATA — it will fail
  //   silently, and I will assume the thing I am looking at is it.
  //   (The old Foundry shipped a `glowRect` DECOR entry that the
  //   renderer has never known how to draw. It was dead art the
  //   entire time and nobody noticed, because a kind that doesn't
  //   exist doesn't error. It just doesn't draw.)
  //
  //   The extra keys below (heat, tickets, ...) are HUD vitals. The
  //   HUD may read them or ignore them; an un-updated HUD still
  //   renders fine. That is the difference between an OPTIONAL
  //   channel and a broken one.
  lamps(engine) {
    const s = this._s();

    // The ghost door LIGHTS UP once the gate is open. The secret
    // stops being a secret the moment you've earned the right to
    // use it — a lock you cannot see is a lock you cannot plan for,
    // and this table does not do "gotcha."
    const litDoor = s.gateOpen ? ["ght_door"] : [];

    return {
      // lit drop-targets + the live hammer + the open door, one channel
      targets: [...s.lit, ...(s.hot ? [s.hot] : []), ...litDoor],
      crucible: s.crucibleLit,
      crucibleTimer: s.crucibleTimer,
      jackpots: s.jackpots,

      // ★ the midway's vitals
      tickets: s.tickets.size,
      ticketMax: TICK_TOTAL,
      gateOpen: s.gateOpen,
      roar: s.roar,
      vaults: s.vaults,

      // ★★★ the rides
      loops: s.loops,
      sideshow: s.sideshow,
      spins: s.spins,

      // the boss's vitals (the being itself rides engine.snapshot().boss)
      heat: s.heat,
      heatMax: HEAT_MAX,
      bossPhase: s.phase,
      meltTimer: s.meltTimer,
      kills: s.kills,
      strikes: s.strikes,
      heartLive: s.phase >= 3,
    };
  },

  // ── Decor — pure render, zero collision ────────────────────
  //
  // ⚠ ONLY `inlay`, `text` AND `hint` EXIST. Playfield.js draws those
  //   three and SILENTLY IGNORES everything else. A decor kind that
  //   doesn't exist fails without a sound. Do not invent one.
  decor: [
    { kind: "text",  x: TW / 2 - 40, y: 100, text: "CIRQUE DE J'YNX", size: 15, tint: "goldDim" },
    // The Ghost Train's housing — art only. The sensor does the work.
    { kind: "inlay", x: VAULT_X, y: 116, w: 76, h: 48, tint: "deck3" },
    { kind: "hint",  x: WILLIE_C.x, y: 144, text: "GHOST", tint: "violetDim" },
    // The Big Top's floor.
    { kind: "inlay", x: WILLIE_C.x - 88, y: WILLIE_C.y - 88, w: 176, h: 176, tint: "deck2" },
    { kind: "hint",  x: WILLIE_C.x, y: TUNNEL_IN_Y + 20, text: "TUNNEL", tint: "ember" },

    // ★★★ THE RIDES — every one of them NAMED.
    //
    // ⚠ A RIDE THE PLAYER CANNOT NAME IS A RIDE THEY WILL NOT AIM
    //   AT. The renderer draws a scoop as a bowl and a hole as a
    //   black void, which tells you what the object DOES — but not
    //   what it's WORTH, and not that it's on the ticket ladder.
    //   The hint is the difference between "a hazard" and "a shot."
    { kind: "hint",  x: 68,  y: 128, text: "WHEEL",    tint: "gold" },
    { kind: "hint",  x: 56,  y: 348, text: "LOOP",     tint: "violet" },
    { kind: "hint",  x: 22,  y: 468, text: "DUNK TANK", tint: "gold" },
    { kind: "hint",  x: 296, y: 400, text: "CANNON",   tint: "violet" },
    { kind: "hint",  x: 66,  y: 240, text: "BELL",     tint: "bone" },
  ],
};

export default FOUNDRY;
